import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import Icon from "@/components/Icon";
import AdminLayout from "@/components/AdminLayout";

const TYPES = ["text", "email", "phone", "url", "dropdown", "date", "image", "checkbox", "longtext"];
const uid = () => "pf_" + Math.random().toString(36).slice(2, 10);

export default function AdminProfileFields() {
  const [fields, setFields] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/profile-fields").then(({ data }) => setFields(data.fields || []))
      .catch((err) => { if (err.response?.status === 403) setForbidden(true); else setFields([]); });
  }, []);

  const setF = (i, patch) => setFields(fields.map((f, j) => j === i ? { ...f, ...patch } : f));
  const move = (i, dir) => { const a = [...fields]; const j = i + dir; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; setFields(a); };
  const add = () => setFields([...(fields || []), { id: uid(), label: "", type: "text", required: false, visible: true, options: [], in_vcard: false }]);

  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/admin/profile-fields", { fields }); setFields(data.fields); toast.success("Profile fields saved"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <AdminLayout title="Customer Profile Fields" subtitle="Owner-only. Define custom fields shown on every customer's profile.">
      {forbidden ? (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-600 px-4 py-3 text-sm">Only the Owner can manage profile fields.</div>
      ) : !fields ? (
        <div className="grid place-items-center py-20"><Icon name="Loader2" className="h-7 w-7 animate-spin text-primary" /></div>
      ) : (
        <div className="space-y-4 max-w-3xl" data-testid="admin-profile-fields">
          {fields.length === 0 && <p className="text-sm text-muted-foreground">No custom fields yet. Add one below.</p>}
          {fields.map((f, i) => (
            <div key={f.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`pf-row-${i}`}>
              <div className="flex items-start gap-3">
                <div className="flex flex-col pt-6">
                  <button onClick={() => move(i, -1)} className="text-muted-foreground hover:text-foreground"><Icon name="ChevronUp" className="h-4 w-4" /></button>
                  <button onClick={() => move(i, 1)} className="text-muted-foreground hover:text-foreground"><Icon name="ChevronDown" className="h-4 w-4" /></button>
                </div>
                <div className="flex-1 grid sm:grid-cols-2 gap-3">
                  <div><label className="text-xs font-semibold text-muted-foreground">Label</label><input data-testid={`pf-label-${i}`} value={f.label} onChange={(e) => setF(i, { label: e.target.value })} className="inp" /></div>
                  <div><label className="text-xs font-semibold text-muted-foreground">Type</label><select data-testid={`pf-type-${i}`} value={f.type} onChange={(e) => setF(i, { type: e.target.value })} className="inp">{TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></div>
                  {f.type === "dropdown" && (
                    <div className="sm:col-span-2"><label className="text-xs font-semibold text-muted-foreground">Options (comma-separated)</label><input value={(f.options || []).join(", ")} onChange={(e) => setF(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} className="inp" /></div>
                  )}
                  <div className="sm:col-span-2 flex flex-wrap gap-4 text-sm">
                    <label className="flex items-center gap-1.5"><input type="checkbox" data-testid={`pf-required-${i}`} checked={!!f.required} onChange={(e) => setF(i, { required: e.target.checked })} />Required</label>
                    <label className="flex items-center gap-1.5"><input type="checkbox" data-testid={`pf-visible-${i}`} checked={f.visible !== false} onChange={(e) => setF(i, { visible: e.target.checked })} />Visible to customers</label>
                    <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!f.in_vcard} onChange={(e) => setF(i, { in_vcard: e.target.checked })} />Include in vCard/landing</label>
                  </div>
                </div>
                <button onClick={() => setFields(fields.filter((_, j) => j !== i))} className="h-9 w-9 grid place-items-center rounded-lg border border-red-500/40 text-red-500 hover:bg-red-500/10 mt-5"><Icon name="Trash2" className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
          <div className="flex gap-2">
            <button data-testid="pf-add" onClick={add} className="rounded-xl border border-border px-4 py-2 text-sm font-semibold hover:bg-accent">+ Add field</button>
            <button data-testid="pf-save" onClick={save} disabled={busy} className="rounded-xl bg-primary text-primary-foreground px-5 py-2 text-sm font-bold hover:opacity-90 disabled:opacity-60">Save fields</button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
